import { capitalize } from 'lodash';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { createAsyncSelectors } from '@lib/selectors';
import { DeleteOutlined } from '@ant-design/icons';
import { enumStringKeys } from '@sonar-software/sonar-ts-lib';
import { SonarText } from '@ui/text';
import { When } from 'react-if';

import { Button, Form, FormListFieldData, Input, Select } from 'antd';
import {
	ClassificationLevel,
	coreActions,
	SetupAuditTrailConditionalLogicRule,
	SetupAuditTrailRuleTypes
} from '@sonar-software/sonar-core';
import React, { useCallback, useMemo } from 'react';
import { useAction, useAsyncState, useOnMount, useSalesforceUsers } from '@hooks';

export interface SetupAuditTrailRuleProps {
    canDelete: boolean,
    field: FormListFieldData,
    ruleType: SetupAuditTrailRuleTypes,
    onDelete?: () => void,
}

const operators: Record<string, string> = {
	action: COPY_SPECIFIC.CONTAINS,
	classification: COPY_SPECIFIC.IS_ANY,
	section: COPY_SPECIFIC.EQUALS,
	user: COPY_SPECIFIC.IS_ANY
};

const sectionsSelectors = createAsyncSelectors((state) => state.core.dataSecurity.setupAuditTrail.sections.get);

export const SetupAuditTrailRule: React.FC<SetupAuditTrailRuleProps> = ({
	field,
	canDelete,
	onDelete,
	ruleType
}) => {
	const { data: sectionData, isRequested: sectionRequested } = useAsyncState(sectionsSelectors);
	const userData = useSalesforceUsers();

	const form = Form.useFormInstance();
	const selectedCondition = Form.useWatch([ 'monitorConfig', 'rules', field.name, 'condition' ]);

	const sectionOptions = useMemo(() => sectionData?.map((section) => ({
		label: section,
		value: section
	})) ?? [], [ sectionData ]);

	const classificationOptions = useMemo(() => enumStringKeys(ClassificationLevel).reverse()
		.map((classification) => ({
			label: classification,
			value: classification
		})), []);

	const userOptions = useMemo(
		() => userData?.map((user) => ({
			label: user.username,
			value: user.username
		})) ?? [],
		[ userData ]
	);

	const getSetupAuditTrailSections = useAction(coreActions.requestGetCoreSetupAuditTrailSections);

	useOnMount(() => {
		getSetupAuditTrailSections();
	});

	const getConditionOptions = useCallback(() => {
		const currentRuleConditions = form.getFieldValue([ 'monitorConfig', 'rules' ]).reduce(
			(previous:Array<string>, current:SetupAuditTrailConditionalLogicRule, index: number) => {
				if (current && index !== field.name) {
					return [ ...previous, current.condition ];
				}

				return [ ...previous ];
			}, []
		);

		let conditionOptions = [
			{
				label: COPY_SPECIFIC.ACTION,
				value: 'action'
			}
		];

		if (!currentRuleConditions?.includes('section')) {
			conditionOptions = [ {
				label: COPY_SPECIFIC.SECTION,
				value: 'section'
			}, ...conditionOptions ];
		}

		if (!currentRuleConditions?.includes('classification')) {
			conditionOptions = [ {
				label: COPY_SPECIFIC.CLASSIFICATION,
				value: 'classification'
			}, ...conditionOptions ];
		}

		if (!currentRuleConditions?.includes('user')) {
			conditionOptions = [
				...conditionOptions,
				{
					label: COPY_SPECIFIC.USER,
					value: 'user'
				}
			];
		}

		return conditionOptions;

	}, [ form, field ]);

	const initialCondition = useMemo(() => {
		const currentRuleConditions = form.getFieldValue([ 'monitorConfig', 'rules' ]).reduce(
			(previous:Array<string>, current:SetupAuditTrailConditionalLogicRule) => {
				if (current) {
					return [ ...previous, current.condition ];
				}

				return [ ...previous ];
			}, []
		);

		if (!currentRuleConditions?.includes('classification')) {
			return 'classification';
		}

		if (!currentRuleConditions?.includes('section')) {
			return 'section';
		}

		if (!currentRuleConditions?.includes('user')) {
			return 'user';
		}

		return 'action';
	}, [ form ]);

	const getDefaultValue = useCallback((newCondition: string) => {
		if (newCondition === 'classification') {
			return [ ClassificationLevel.Critical ];
		}

		if (newCondition === 'section') {
			return sectionOptions.length > 0 ? [ sectionOptions[ 0 ].value ] : undefined;
		}

		return [];
	}, [ sectionOptions ]);

	const onConditionChange = useCallback((condition: string) => {
		form.setFieldValue([ 'monitorConfig', 'rules', field.name, 'value' ], getDefaultValue(condition));
	}, [ field, form, getDefaultValue ]);

	return (
		<>
			<div>
				<When condition={ field.name !== 0 }>
					<SonarText>{ capitalize(ruleType) }</SonarText>
				</When>
			</div>
			<Form.Item
				initialValue={ initialCondition }
				name={ [ field.name, 'condition' ] }>
				<Select
					onChange={ onConditionChange }
					options={ getConditionOptions() } />
			</Form.Item>
			<div>
				<SonarText>{ selectedCondition ? operators[ selectedCondition ] : '' }</SonarText>
			</div>
			<When condition={ selectedCondition === 'action' }>
				<Form.Item
					name={ [ field.name, 'value', 0 ] }>
					<Input />
				</Form.Item>
			</When>
			<When condition={ selectedCondition === 'classification' }>
				<Form.Item
					initialValue={ getDefaultValue(initialCondition) }
					name={ [ field.name, 'value' ] }>
					<Select
						mode='multiple'
						options={ classificationOptions } />
				</Form.Item>
			</When>
			<When condition={ selectedCondition === 'section' }>
				<Form.Item
					initialValue={ getDefaultValue(initialCondition) }
					name={ [ field.name, 'value' ] }>
					<Select
						loading={ sectionRequested }
						mode='multiple'
						options={ sectionOptions }
						placeholder={ COPY_SPECIFIC.NO_SECTIONS_AVAILABLE } />
				</Form.Item>
			</When>
			<When condition={ selectedCondition === 'user' }>
				<Form.Item
					initialValue={ getDefaultValue(initialCondition) }
					name={ [ field.name, 'value' ] }
					required>
					<Select
						mode='multiple'
						options={ userOptions }
						placeholder={ COPY_SPECIFIC.SELECT_USER } />
				</Form.Item>
			</When>
			<div>
				<When condition={ canDelete }>
					<Button
						className='delete-rule-button'
						icon={ <DeleteOutlined /> }
						onClick={ onDelete }
						style={ { alignSelf: 'flex-start' } } />
				</When>
			</div>
		</>
	);
};
